import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import { ApiResponse, ApiErrorResponse } from 'types/apiTypes';
import { InvitationConfig, VisitorInvitationPayload, InvitationDocument, CreateVisitorInvitationResponse, InvitationCustomField } from 'types/visitorInvitationTypes';
import { fetchVisits } from './visitsSlice';
import i18n from 'i18n/i18n';

interface VisitorInvitationState {
    invitationConfig: InvitationConfig | null;
    invitationCustomFields: InvitationCustomField[];
    invitationDocuments: ApiResponse<InvitationDocument>;
    invitationSuccess: boolean;
    loadingInvitationConfig: boolean;
    loadingInvitationCustomFields: boolean;
    loadingCreateInvitation: boolean;
    loadingUpdateInvitationConfig: boolean;
    loadingInvitationDocuments: boolean;
    error: string | null;
}

const initialState: VisitorInvitationState = {
    invitationConfig: null,
    invitationCustomFields: [],
    invitationDocuments: {
        data: [],
        meta: null,
        totalCount: 0,
        filteredCount: 0,
    },
    invitationSuccess: false,
    loadingInvitationConfig: false,
    loadingInvitationCustomFields: false,
    loadingCreateInvitation: false,
    loadingUpdateInvitationConfig: false,
    loadingInvitationDocuments: false,
    error: null,
};

// Retrieve the base URL from environment variables
const apiUrl = process.env.REACT_APP_BACKEND_URL;

if (!apiUrl) {
    console.error('REACT_APP_BACKEND_URL is not set');
}

export const createVisitorInvitation = createAsyncThunk<ApiResponse<CreateVisitorInvitationResponse>, { orgId: number; payload: VisitorInvitationPayload }, { rejectValue: string; dispatch: any }>(
    'visitorInvitation/createVisitorInvitation',
    async ({ orgId, payload }, { dispatch, rejectWithValue }) => {
        try {
            const response = await axios.post(`${apiUrl}/orgs/${orgId}/visitorInvitation?language=${i18n.language.split('-')[0]}`, payload, {
                headers: {
                    Authorization: `${localStorage.getItem('authToken')}`,
                    Accept: 'application/json',
                },
            });
            dispatch(fetchVisits({ orgId }));
            return response.data;
        } catch (error) {
            const axiosError = error as AxiosError<ApiErrorResponse>;
            return rejectWithValue(axiosError.response?.data.message || 'Failed to create visitor invitation');
        }
    }
);

export const fetchInvitationConfig = createAsyncThunk<ApiResponse<InvitationConfig>, { orgId: number }, { rejectValue: string }>(
    'visitorInvitation/fetchInvitationConfig',
    async ({ orgId }, { rejectWithValue }) => {
        try {
            const response = await axios.get<ApiResponse<InvitationConfig>>(`${apiUrl}/orgs/${orgId}/visitorInvitationConfig`, {
                headers: {
                    Authorization: `${localStorage.getItem('authToken')}`,
                    Accept: 'application/json',
                },
            });
            return response.data;
        } catch (error) {
            const axiosError = error as AxiosError<ApiErrorResponse>;
            return rejectWithValue(axiosError.response?.data.message || 'Failed to fetch invitation config');
        }
    }
);

export const updateInvitationConfig = createAsyncThunk<void, { orgId: number, config: Partial<InvitationConfig> }, { rejectValue: string; dispatch: any }>(
    'visitorInvitation/updateInvitationConfig',
    async ({ orgId, config }, { dispatch, rejectWithValue }) => {
        try {
            await axios.patch(`${apiUrl}/orgs/${orgId}/visitorInvitationConfig`, config, {
                headers: {
                    Authorization: `${localStorage.getItem('authToken')}`,
                    Accept: 'application/json',
                },
            });
            dispatch(fetchInvitationConfig({ orgId }));
        } catch (error) {
            const axiosError = error as AxiosError<ApiErrorResponse>;
            return rejectWithValue(axiosError.response?.data.message || 'Failed to update invitation config');
        }
    }
);

export const fetchInvitationDocuments = createAsyncThunk<ApiResponse<InvitationDocument>, { orgId: number }, { rejectValue: string }>(
    'visitorInvitation/fetchInvitationDocuments',
    async ({ orgId }, { rejectWithValue }) => {
        try {
            const response = await axios.get<ApiResponse<InvitationDocument>>(`${apiUrl}/orgs/${orgId}/visitorInvitationDocument`, {
                headers: {
                    Authorization: `${localStorage.getItem('authToken')}`,
                    Accept: 'application/json',
                },
            });
            return response.data;
        } catch (error) {
            const axiosError = error as AxiosError<ApiErrorResponse>;
            return rejectWithValue(axiosError.response?.data.message || 'Failed to fetch invitation documents');
        }
    }
);

export const updateInvitationDocuments = createAsyncThunk<void, { orgId: number, documentIds?: number[] }, { rejectValue: string; dispatch: any }>(
    'visitorInvitation/updateInvitationDocuments',
    async ({ orgId, documentIds }, { dispatch, rejectWithValue }) => {
        try {
            const payload = documentIds?.length ? { id: documentIds } : {};
            await axios.patch(`${apiUrl}/orgs/${orgId}/visitorInvitationDocument`, payload, {
                headers: {
                    Authorization: `${localStorage.getItem('authToken')}`,
                    Accept: 'application/json',
                },
            });
            dispatch(fetchInvitationDocuments({ orgId }));
        } catch (error) {
            const axiosError = error as AxiosError<ApiErrorResponse>;
            return rejectWithValue(axiosError.response?.data.message || 'Failed to update invitation documents');
        }
    }
);

export const createInvitationCustomFields = createAsyncThunk<
    void,
    { orgId: number; fieldId: number; required: number },
    { rejectValue: string; dispatch: any }
>(
    'visitorInvitation/createInvitationCustomFields',
    async ({ orgId, fieldId, required }, { dispatch, rejectWithValue }) => {
        try {
            await axios.post<ApiResponse<InvitationCustomField>>(`${apiUrl}/orgs/${orgId}/visitorInvitationConfig/fieldCustom/${fieldId}`, {
                required: String(required),
            }, {
                headers: {
                    Authorization: `${localStorage.getItem('authToken')}`,
                    Accept: 'application/json',
                },
            });
        } catch (error) {
            const axiosError = error as AxiosError<ApiErrorResponse>;
            return rejectWithValue(axiosError.response?.data.message || 'Failed to create custom fields');
        }
    }
);

export const fetchInvitationCustomFields = createAsyncThunk<
    InvitationCustomField[],
    { orgId: number },
    { rejectValue: string }
>(
    'visitorInvitation/fetchInvitationCustomFields',
    async ({ orgId }, { rejectWithValue }) => {
        try {
            const response = await axios.get<ApiResponse<InvitationCustomField>>(`${apiUrl}/orgs/${orgId}/visitorInvitationConfig/fieldCustom`, {
                headers: {
                    Authorization: `${localStorage.getItem('authToken')}`,
                    Accept: 'application/json',
                },
            });
            return response.data.data;
        } catch (error) {
            const axiosError = error as AxiosError<ApiErrorResponse>;
            return rejectWithValue(axiosError.response?.data.message || 'Failed to fetch custom fields');
        }
    }
);

export const updateInvitationCustomFields = createAsyncThunk<
    void,
    { orgId: number; fieldId: number; required: number },
    { rejectValue: string; dispatch: any }
>(
    'visitorInvitation/updateInvitationCustomFields',
    async ({ orgId, fieldId, required }, { dispatch, rejectWithValue }) => {
        try {
            await axios.patch<ApiResponse<InvitationCustomField>>(`${apiUrl}/orgs/${orgId}/visitorInvitationConfig/fieldCustom/${fieldId}`, {
                required: String(required),
            }, {
                headers: {
                    Authorization: `${localStorage.getItem('authToken')}`,
                    Accept: 'application/json',
                },
            });
        } catch (error) {
            const axiosError = error as AxiosError<ApiErrorResponse>;
            return rejectWithValue(axiosError.response?.data.message || 'Failed to update custom fields');
        }
    }
);

export const deleteInvitationCustomFields = createAsyncThunk<
    void,
    { orgId: number; fieldId: number },
    { rejectValue: string; dispatch: any }
>(
    'visitorInvitation/deleteInvitationCustomFields',
    async ({ orgId, fieldId }, { dispatch, rejectWithValue }) => {
        try {
            await axios.delete<ApiResponse<InvitationCustomField>>(`${apiUrl}/orgs/${orgId}/visitorInvitationConfig/fieldCustom/${fieldId}`, {
                headers: {
                    Authorization: `${localStorage.getItem('authToken')}`,
                    Accept: 'application/json',
                },
            });
        } catch (error) {
            const axiosError = error as AxiosError<ApiErrorResponse>;
            return rejectWithValue(axiosError.response?.data.message || 'Failed to update custom fields');
        }
    }
);

const visitorInvitationSlice = createSlice({
    name: 'visitorInvitation',
    initialState,
    reducers: {
        resetState: (state) => {
            state.loadingInvitationConfig = false;
            state.loadingCreateInvitation = false;
            state.loadingUpdateInvitationConfig = false;
            state.loadingInvitationDocuments = false;
            state.error = null;
            state.invitationSuccess = false;
        },
        clearVisitorInvitationState() {
            return initialState;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(createVisitorInvitation.pending, (state) => {
                state.loadingCreateInvitation = true;
                state.error = null;
                state.invitationSuccess = false;
            })
            .addCase(createVisitorInvitation.fulfilled, (state) => {
                state.loadingCreateInvitation = false;
                state.invitationSuccess = true;
            })
            .addCase(createVisitorInvitation.rejected, (state, action) => {
                state.loadingCreateInvitation = false;
                state.error = action.payload || 'Failed to create visitor invitation';
                state.invitationSuccess = false;
            })
            .addCase(fetchInvitationConfig.pending, (state) => {
                state.loadingInvitationConfig = true;
                state.error = null;
            })
            .addCase(fetchInvitationConfig.fulfilled, (state, action) => {
                state.loadingInvitationConfig = false;
                state.invitationConfig = action.payload.data[0];
            })
            .addCase(fetchInvitationConfig.rejected, (state, action) => {
                state.loadingInvitationConfig = false;
                state.error = action.payload || 'Failed to fetch invitation config';
            })
            .addCase(updateInvitationConfig.pending, (state) => {
                state.loadingUpdateInvitationConfig = true;
                state.error = null;
            })
            .addCase(updateInvitationConfig.fulfilled, (state) => {
                state.loadingUpdateInvitationConfig = false;
            })
            .addCase(updateInvitationConfig.rejected, (state, action) => {
                state.loadingUpdateInvitationConfig = false;
                state.error = action.payload || 'Failed to update invitation config';
            })
            .addCase(fetchInvitationDocuments.pending, (state) => {
                state.loadingInvitationDocuments = true;
                state.error = null;
            })
            .addCase(fetchInvitationDocuments.fulfilled, (state, action) => {
                state.loadingInvitationDocuments = false;
                state.invitationDocuments = action.payload;
            })
            .addCase(fetchInvitationDocuments.rejected, (state, action) => {
                state.loadingInvitationDocuments = false;
                state.error = action.payload || 'Failed to fetch invitation documents';
            })
            .addCase(updateInvitationDocuments.pending, (state) => {
                state.loadingInvitationDocuments = true;
                state.error = null;
            })
            .addCase(updateInvitationDocuments.fulfilled, (state) => {
                state.loadingInvitationDocuments = false;
            })
            .addCase(updateInvitationDocuments.rejected, (state, action) => {
                state.loadingInvitationDocuments = false;
                state.error = action.payload || 'Failed to update invitation documents';
            })
            .addCase(createInvitationCustomFields.pending, (state) => {
                state.loadingInvitationCustomFields = true;
                state.error = null;
            })
            .addCase(createInvitationCustomFields.fulfilled, (state) => {
                state.loadingInvitationCustomFields = false;
            })
            .addCase(createInvitationCustomFields.rejected, (state, action) => {
                state.loadingInvitationCustomFields = false;
                state.error = action.payload || 'Failed to create invitation custom fields';
            })
            .addCase(fetchInvitationCustomFields.pending, (state) => {
                state.loadingInvitationCustomFields = true;
                state.error = null;
            })
            .addCase(fetchInvitationCustomFields.fulfilled, (state, action) => {
                state.loadingInvitationCustomFields = false;
                state.invitationCustomFields = action.payload;
            })
            .addCase(fetchInvitationCustomFields.rejected, (state, action) => {
                state.loadingInvitationCustomFields = false;
                state.error = action.payload || 'Failed to fetch invitation custom fields';
            })
            .addCase(updateInvitationCustomFields.pending, (state) => {
                state.loadingInvitationCustomFields = true;
                state.error = null;
            })
            .addCase(updateInvitationCustomFields.fulfilled, (state) => {
                state.loadingInvitationCustomFields = false;
            })
            .addCase(updateInvitationCustomFields.rejected, (state, action) => {
                state.loadingInvitationCustomFields = false;
                state.error = action.payload || 'Failed to update invitation custom fields';
            })
            .addCase(deleteInvitationCustomFields.pending, (state) => {
                state.loadingInvitationCustomFields = true;
                state.error = null;
            })
            .addCase(deleteInvitationCustomFields.fulfilled, (state) => {
                state.loadingInvitationCustomFields = false;
            })
            .addCase(deleteInvitationCustomFields.rejected, (state, action) => {
                state.loadingInvitationCustomFields = false;
                state.error = action.payload || 'Failed to update invitation custom fields';
            })
    },
});

export const { resetState, clearVisitorInvitationState } = visitorInvitationSlice.actions;

export default visitorInvitationSlice.reducer;
